<script setup lang="ts">
import { dayjs } from '@revolugo/common/utils'
import { BookingStatus } from '@revolugo/graphql/private'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { RButton } from '#atoms'
import {
  RAlert,
  RCancellationPoliciesList,
  RCancellationPoliciesTimeline,
} from '#molecules'
import { RAlertSeverity } from '#types'

import type { CancellationPolicySummaryProps } from './CancellationPoliciesSummary.types.ts'

const { t } = useI18n()

const emit = defineEmits<{
  cancelBooking: []
  payBooking: []
}>()

const props = defineProps<CancellationPolicySummaryProps>()

const isBookingCheckInDatePassed = computed(() =>
  dayjs().isAfter(props.bookingCheckInDate),
)
</script>

<template>
  <div>
    <h6 class="text-xl font-medium text-neutral-950">
      {{ t('title') }}
    </h6>
    <div class="mb-6 mt-4">
      <RCancellationPoliciesList
        :cancellation-policies="cancellationPolicies"
        :price="price"
        :currency="currency"
        :booking-status="bookingStatus"
      />
    </div>
    <div
      class="mb-4"
      v-if="
        bookingStatus !== BookingStatus.Canceled && !isBookingCheckInDatePassed
      "
    >
      <RCancellationPoliciesTimeline
        :cancellation-policies="cancellationPolicies"
      />
    </div>
    <p
      class="text-sm mb-4"
      data-testid="r-cancellation-policies-summary-timezone-disclaimer"
    >
      {{ t('timezone', { timezone }) }}
    </p>
    <RButton
      v-if="withPayButton"
      class="mb-4"
      data-testid="pay-booking-cta"
      @click="emit('payBooking')"
      fluid
    >
      {{ t('actions.pay') }}
    </RButton>
    <RAlert
      :severity="RAlertSeverity.Error"
      icon="icon-[ph--info]"
      v-if="bookingStatus === BookingStatus.Canceled"
      data-testid="booking-canceled-alert"
    >
      <p class="text-neutral-950">
        {{ t('alert.noModification') }}
      </p>
    </RAlert>
    <RAlert
      :severity="RAlertSeverity.Error"
      icon="icon-[ph--info]"
      v-else-if="isBookingCheckInDatePassed"
      data-testid="booking-check-in-date-passed-alert"
    >
      <p class="text-neutral-950">
        {{ t('alert.checkInDatePassed') }}
      </p>
    </RAlert>
    <div
      v-else-if="
        [BookingStatus.PendingPayment, BookingStatus.Confirmed].includes(
          bookingStatus,
        )
      "
    >
      <RButton
        data-testid="cancel-booking-cta"
        @click="emit('cancelBooking')"
        severity="danger"
        outlined
        fluid
      >
        {{ t('cancelDialog.actions.confirm') }}
      </RButton>
    </div>
  </div>
</template>

<i18n lang="json" src="./CancellationPoliciesSummary.i18n.json"></i18n>
